import React, { useState, useEffect } from 'react';
import {
  Upload,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  X,
  FileText,
  Database,
  ShieldCheck,
  ArrowRight,
  Filter,
  Check,
  AlertOctagon,
  History,
  FileSpreadsheet
} from 'lucide-react';
import { Client } from '../types';
import { playPulseSeal } from '../utils/pulseAudio';

const SUPPORTED_WELLSKY_EXTENSIONS = ['.csv', '.tsv', '.json', '.txt'];

type DroppedWellSkyFile = { label: string; file: File };

function isSupportedWellSkyFile(name: string): boolean {
  const dot = name.lastIndexOf('.');
  const extension = dot >= 0 ? name.slice(dot).toLowerCase() : '';
  return SUPPORTED_WELLSKY_EXTENSIONS.includes(extension);
}

function readFileText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error || new Error('Could not read that file.'));
    reader.readAsText(file);
  });
}

function entryToFile(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

function readDirectory(entry: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const reader = entry.createReader();
    const all: FileSystemEntry[] = [];
    const readBatch = () => {
      reader.readEntries((batch) => {
        if (batch.length === 0) {
          resolve(all);
          return;
        }
        all.push(...batch);
        readBatch();
      }, reject);
    };
    readBatch();
  });
}

async function filesFromEntry(entry: FileSystemEntry, pathPrefix: string): Promise<DroppedWellSkyFile[]> {
  if (entry.isFile) {
    const file = await entryToFile(entry as FileSystemFileEntry);
    const label = `${pathPrefix}${file.name}`;
    return [{ label, file }];
  }
  if (entry.isDirectory) {
    const children = await readDirectory(entry as FileSystemDirectoryEntry);
    const nested = await Promise.all(
      children.map((child) => filesFromEntry(child, `${pathPrefix}${entry.name}/`))
    );
    return nested.flat();
  }
  return [];
}

async function filesFromDataTransfer(transfer: DataTransfer): Promise<{ files: DroppedWellSkyFile[]; sawDirectory: boolean }> {
  const entries = Array.from(transfer.items || [])
    .map((item) => (item as DataTransferItem & { webkitGetAsEntry?: () => FileSystemEntry | null }).webkitGetAsEntry?.() || null)
    .filter((entry): entry is FileSystemEntry => Boolean(entry));
  if (entries.length > 0) {
    const groups = await Promise.all(entries.map((entry) => filesFromEntry(entry, '')));
    return {
      files: groups.flat(),
      sawDirectory: entries.some((entry) => entry.isDirectory)
    };
  }
  return {
    files: Array.from(transfer.files || []).map((file) => ({ label: file.name, file })),
    sawDirectory: false
  };
}

interface WellSkyIngestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  clients: Client[];
}

export const WellSkyIngestionModal: React.FC<WellSkyIngestionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  clients
}) => {
  const [activeTab, setActiveTab] = useState<'INGEST' | 'EXCEPTIONS' | 'HISTORY'>('INGEST');
  const [reportType, setReportType] = useState<'AUTO' | 'AUTHORIZATION' | 'DOS' | 'EOP'>('AUTO');
  const [filename, setFilename] = useState('wellsky_production_export.csv');
  const [rawContent, setRawContent] = useState('');
  const [dragDepth, setDragDepth] = useState(0);
  const [dropNotice, setDropNotice] = useState('');
  const [folderChoices, setFolderChoices] = useState<DroppedWellSkyFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastRunResult, setLastRunResult] = useState<any | null>(null);

  // Exceptions list
  const [exceptions, setExceptions] = useState<any[]>([]);
  const [loadingExceptions, setLoadingExceptions] = useState(false);
  const [selectedException, setSelectedException] = useState<any | null>(null);
  const [resolutionClientId, setResolutionClientId] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');

  // History list
  const [pastRuns, setPastRuns] = useState<any[]>([]);
  const [loadingPastRuns, setLoadingPastRuns] = useState(false);

  const fetchExceptions = async () => {
    try {
      setLoadingExceptions(true);
      const res = await fetch('/api/wellsky-import/exceptions');
      if (res.ok) {
        setExceptions(await res.json());
      }
    } catch (err) {
      console.error('Error fetching exceptions:', err);
    } finally {
      setLoadingExceptions(false);
    }
  };

  const fetchPastRuns = async () => {
    try {
      setLoadingPastRuns(true);
      const res = await fetch('/api/wellsky-import/runs');
      if (res.ok) {
        setPastRuns(await res.json());
      }
    } catch (err) {
      console.error('Error fetching past runs:', err);
    } finally {
      setLoadingPastRuns(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchExceptions();
      fetchPastRuns();
    } else {
      setDragDepth(0);
      setDropNotice('');
      setFolderChoices([]);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const loadPreviewFile = async (file: File, label: string) => {
    const text = await readFileText(file);
    const baseName = label.split(/[/\\]/).pop() || file.name;
    setFilename(baseName);
    setRawContent(text);
    setDropNotice(`Loaded ${label} into the preview. Nothing is imported until you choose Execute.`);
  };

  const handleBrowseFile = async (file: File | undefined) => {
    if (!file) return;
    if (!isSupportedWellSkyFile(file.name)) {
      setDropNotice(`“${file.name}” is not a supported WellSky file. Use a .csv, .tsv, .json, or .txt export.`);
      return;
    }
    setFolderChoices([]);
    try {
      await loadPreviewFile(file, file.name);
    } catch {
      setDropNotice('Could not read that file.');
    }
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragDepth(0);
    setFolderChoices([]);
    setDropNotice('');
    try {
      const { files, sawDirectory } = await filesFromDataTransfer(event.dataTransfer);
      const supported = files.filter((item) => isSupportedWellSkyFile(item.label));
      if (sawDirectory) {
        if (files.length === 0) {
          setDropNotice('This folder is empty.');
          return;
        }
        if (supported.length === 0) {
          setDropNotice('No supported WellSky files in this folder. Use a .csv, .tsv, .json, or .txt export.');
          return;
        }
        setFolderChoices(supported);
        setDropNotice('Select one file to load into the preview. Files are not combined, and nothing is imported until you choose Execute.');
        return;
      }
      if (files.length === 1 && supported.length === 1) {
        await loadPreviewFile(supported[0].file, supported[0].label);
        return;
      }
      if (supported.length === 0) {
        setDropNotice('Unsupported file. Drop a .csv, .tsv, .json, or .txt WellSky export.');
        return;
      }
      setFolderChoices(supported);
      setDropNotice('Select one file to load into the preview. Files are not combined, and nothing is imported until you choose Execute.');
    } catch {
      setDropNotice('Could not read that drop. Try Browse, or drop a .csv, .tsv, .json, or .txt file.');
    }
  };

  const handleExecuteIngestion = async () => {
    if (!rawContent.trim()) {
      alert('Please paste or upload WellSky export content.');
      return;
    }

    try {
      setLoading(true);
      const res = await fetch('/api/wellsky-import/execute', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rawContent,
          filename,
          reportType: reportType === 'AUTO' ? undefined : reportType
        })
      });

      if (res.ok) {
        const result = await res.json();
        setLastRunResult(result);
        playPulseSeal();
        fetchExceptions();
        fetchPastRuns();
        onSuccess();
      } else {
        const err = await res.json();
        alert(err.error || 'Ingestion failed');
      }
    } catch (err: any) {
      alert(`Ingestion error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleResolveException = async () => {
    if (!selectedException) return;
    try {
      setLoading(true);
      const res = await fetch(`/api/wellsky-import/exceptions/${selectedException.id}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selectedClientId: resolutionClientId || undefined,
          notes: resolutionNotes || 'Manually resolved by user'
        })
      });

      if (res.ok) {
        setSelectedException(null);
        setResolutionClientId('');
        setResolutionNotes('');
        await fetchExceptions();
        await fetchPastRuns();
        onSuccess();
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to resolve exception');
      }
    } catch (err: any) {
      alert(`Resolution error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDismissException = async (id: string) => {
    try {
      setLoading(true);
      const res = await fetch(`/api/wellsky-import/exceptions/${id}/dismiss`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: 'Dismissed by administrator' })
      });

      if (res.ok) {
        await fetchExceptions();
        await fetchPastRuns();
        onSuccess();
      }
    } catch (err) {
      console.error('Dismiss exception failed:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-xs">
      <div className="w-full max-w-4xl max-h-[92vh] flex flex-col rounded-2xl border border-slate-200 shadow-2xl overflow-hidden bg-white">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-700 flex justify-between items-center bg-slate-800">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-xl bg-white/10 text-white border border-white/25">
              <FileSpreadsheet className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="text-base font-bold text-white tracking-tight">
                  WellSky Production Ingestion & Staging Center
                </h3>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-white/15 text-white border border-white/30 font-bold">
                  PORTED PIPELINE
                </span>
              </div>
              <p className="text-xs text-white mt-0.5">
                Duplicate-safe ingestion, original file preservation, staging exception queue, and strict reconciliation equation verification.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Bar */}
        <div className="flex border-b border-slate-200 px-6 bg-white text-xs font-semibold">
          <button
            onClick={() => setActiveTab('INGEST')}
            className={`py-3 px-4 flex items-center space-x-2 border-b-2 transition-colors ${
              activeTab === 'INGEST'
                ? 'border-slate-800 text-slate-900 font-bold'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            <Upload className="w-4 h-4" />
            <span>Ingest & Execute</span>
          </button>
          <button
            onClick={() => {
              setActiveTab('EXCEPTIONS');
              fetchExceptions();
            }}
            className={`py-3 px-4 flex items-center space-x-2 border-b-2 transition-colors ${
              activeTab === 'EXCEPTIONS'
                ? 'border-slate-800 text-slate-900 font-bold'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            <AlertTriangle className="w-4 h-4" />
            <span>Staged Exception Queue</span>
            {exceptions.length > 0 && (
              <span className="ml-1 text-[10px] font-mono px-1.5 py-0.2 rounded-full bg-amber-100 text-amber-900 font-bold">
                {exceptions.length}
              </span>
            )}
          </button>
          <button
            onClick={() => {
              setActiveTab('HISTORY');
              fetchPastRuns();
            }}
            className={`py-3 px-4 flex items-center space-x-2 border-b-2 transition-colors ${
              activeTab === 'HISTORY'
                ? 'border-slate-800 text-slate-900 font-bold'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            <History className="w-4 h-4" />
            <span>Import Runs & Equations</span>
            <span className="text-[10px] font-mono text-slate-600 font-normal">
              ({pastRuns.length})
            </span>
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5 bg-white">
          {activeTab === 'INGEST' && (
            <div className="space-y-4">
              {/* Top controls: Report Type & Sample Load */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-3.5 rounded-xl bg-white border border-slate-200 space-y-2">
                  <label className="text-xs font-semibold text-slate-900 flex items-center justify-between">
                    <span>1. Select Report Type</span>
                    <span className="text-[10px] font-mono text-slate-700">Schema Validation Enforced</span>
                  </label>
                  <div className="grid grid-cols-4 gap-1.5 text-xs font-mono">
                    {(['AUTO', 'AUTHORIZATION', 'DOS', 'EOP'] as const).map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setReportType(t)}
                        className={`py-1.5 px-2 rounded text-center transition-all border ${
                          reportType === t
                            ? 'bg-slate-800 text-white font-bold border-slate-700 shadow-xs'
                            : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50 hover:text-slate-900'
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="p-3.5 rounded-xl bg-white border border-slate-200 space-y-2">
                  <label className="text-xs font-semibold text-slate-900 flex items-center justify-between">
                    <span>2. Ingestion Source</span>
                    <span className="text-[10px] text-slate-700 font-mono">Authentic Files Only</span>
                  </label>
                  <p className="text-[11px] text-slate-700 leading-relaxed">
                    Upload or paste an authentic WellSky export file (.csv or .tsv). Simulated fixtures and hardcoded sample datasets are strictly prohibited.
                  </p>
                </div>
              </div>

              <div
                onDragEnter={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setDragDepth((depth) => depth + 1);
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onDragLeave={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setDragDepth((depth) => Math.max(0, depth - 1));
                }}
                onDrop={handleDrop}
                className={`rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors ${
                  dragDepth > 0 ? 'border-slate-800 bg-slate-50' : 'border-slate-300 bg-white'
                }`}
              >
                <FileText className="w-5 h-5 mx-auto text-slate-800" />
                <p className="mt-2 text-sm font-semibold text-slate-900">Drop a WellSky file or folder here</p>
                <p className="mt-1 text-[11px] text-slate-700">
                  A supported file loads into the preview. A folder lists files so you can choose one.
                </p>
              </div>

              {dropNotice && (
                <p className="text-xs text-slate-900 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                  {dropNotice}
                </p>
              )}

              {folderChoices.length > 0 && (
                <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                  <p className="px-3 py-2 text-xs font-semibold text-slate-900 border-b border-slate-200 bg-slate-50">
                    Select one file
                  </p>
                  {folderChoices.map((choice) => (
                    <button
                      key={choice.label}
                      type="button"
                      onClick={() => {
                        loadPreviewFile(choice.file, choice.label).catch(() => setDropNotice('Could not read that file.'));
                      }}
                      className="w-full text-left px-3 py-2 text-xs text-slate-900 hover:bg-slate-50 border-t border-slate-200"
                    >
                      {choice.label}
                    </button>
                  ))}
                </div>
              )}

              {/* File upload and input box */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <label className="text-xs font-semibold text-slate-900">
                      Raw File Content (CSV, TSV, or JSON)
                    </label>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-50 text-slate-800 border border-slate-200">
                      File: {filename}
                    </span>
                  </div>

                  <label className="cursor-pointer text-[11px] font-semibold text-slate-700 hover:text-slate-900 flex items-center space-x-1 bg-white hover:bg-slate-50 px-3 py-1 rounded-lg border border-slate-300 transition-colors">
                    <Upload className="w-3.5 h-3.5" />
                    <span>Browse</span>
                    <input
                      type="file"
                      accept=".csv,.tsv,.json,.txt"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        void handleBrowseFile(file);
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>

                <textarea
                  rows={6}
                  value={rawContent}
                  onChange={(e) => setRawContent(e.target.value)}
                  placeholder="Paste authentic WellSky production export text (CSV, TSV, or JSON) here..."
                  className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs font-mono text-slate-900 placeholder:text-slate-500 focus:outline-none focus:border-slate-800 transition-colors"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-1">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleExecuteIngestion}
                    disabled={loading || !rawContent.trim()}
                    className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-white text-xs font-bold flex items-center space-x-2 border border-slate-700 shadow-xs transition-all"
                  >
                    {loading ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4" />
                    )}
                    <span>Execute Production Ingestion</span>
                  </button>

                  <span className="text-[11px] text-slate-700 font-mono">
                    Zero fake demographics • Raw content preserved in Cloud SQL (PostgreSQL)
                  </span>
                </div>

                {lastRunResult && (
                  <button
                    onClick={() => setActiveTab('EXCEPTIONS')}
                    className="text-xs text-amber-800 hover:underline flex items-center space-x-1"
                  >
                    <span>View Staged Exceptions ({exceptions.length})</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Last Ingestion Run Result Details */}
              {lastRunResult && (
                <div className="p-4 rounded-xl bg-white border border-slate-200 space-y-4">
                  {/* Banner & Equation */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-slate-200">
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                          Run: {lastRunResult.runId}
                        </span>
                        <span
                          className={`text-[10px] font-mono px-2 py-0.5 rounded font-bold ${
                            lastRunResult.status === 'COMPLETED'
                              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                              : 'bg-amber-50 text-amber-900 border border-amber-200'
                          }`}
                        >
                          {lastRunResult.status}
                        </span>
                        {lastRunResult.isExactFileReimport && (
                          <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-purple-50 text-purple-900 border border-purple-200">
                            Re-Import (Duplicate Safe)
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-700 font-mono mt-0.5">
                        Source: {lastRunResult.sourceFilename} • SHA-256:{' '}
                        {lastRunResult.fileHash?.slice(0, 16)}...
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <ShieldCheck className="w-4 h-4 text-emerald-700" />
                      <span className="text-xs font-semibold text-slate-800">
                        Equation Valid:{' '}
                        <span className="text-emerald-800 font-mono">
                          {lastRunResult.reconciliationEquationValid ? 'TRUE (100% Accounted)' : 'FALSE'}
                        </span>
                      </span>
                    </div>
                  </div>

                  {/* Mathematical Reconciliation Equation display */}
                  <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 font-mono text-xs text-slate-800 flex items-center justify-between">
                    <span className="text-slate-700 text-[11px]">Equation:</span>
                    <span className="font-bold text-slate-900 tracking-wide">
                      {lastRunResult.reconciliationEquationString}
                    </span>
                  </div>

                  {/* 5-Bucket Breakdown */}
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
                    <div className="p-2.5 rounded-lg bg-white border border-slate-200">
                      <div className="text-[10px] text-slate-600 uppercase">Source Rows</div>
                      <div className="text-lg font-bold font-mono text-slate-900">
                        {lastRunResult.sourceRowsCount}
                      </div>
                    </div>
                    <div className="p-2.5 rounded-lg bg-white border border-slate-200">
                      <div className="text-[10px] text-slate-600 uppercase">Accepted</div>
                      <div className="text-lg font-bold font-mono text-emerald-800">
                        {lastRunResult.acceptedCount}
                      </div>
                    </div>
                    <div className="p-2.5 rounded-lg bg-white border border-slate-200">
                      <div className="text-[10px] text-slate-600 uppercase">Duplicates</div>
                      <div className="text-lg font-bold font-mono text-purple-800">
                        {lastRunResult.duplicateCount}
                      </div>
                    </div>
                    <div className="p-2.5 rounded-lg bg-white border border-slate-200">
                      <div className="text-[10px] text-slate-600 uppercase">Held / Staged</div>
                      <div className="text-lg font-bold font-mono text-amber-800">
                        {lastRunResult.heldCount}
                      </div>
                    </div>
                    <div className="p-2.5 rounded-lg bg-white border border-slate-200">
                      <div className="text-[10px] text-slate-600 uppercase">Rejected</div>
                      <div className="text-lg font-bold font-mono text-red-800">
                        {lastRunResult.rejectedCount}
                      </div>
                    </div>
                  </div>

                  {/* One Exact Chain Proof */}
                  {lastRunResult.oneExactChain && (
                    <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 text-xs space-y-1.5">
                      <div className="font-bold text-slate-900 flex items-center space-x-1.5 text-xs">
                        <Check className="w-3.5 h-3.5 text-slate-700" />
                        <span>Exact End-to-End Chain Verified in Database</span>
                      </div>
                      <div className="font-mono text-[11px] text-slate-800 grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
                        <div>
                          <span className="text-slate-600 block text-[10px]">Client / Member:</span>
                          <strong>{lastRunResult.oneExactChain.memberClient}</strong> (
                          {lastRunResult.oneExactChain.clientId})
                        </div>
                        <div>
                          <span className="text-slate-600 block text-[10px]">Authorization & CPT:</span>
                          <strong>#{lastRunResult.oneExactChain.authNumber}</strong> • CPT{' '}
                          {lastRunResult.oneExactChain.serviceCode}
                        </div>
                        <div>
                          <span className="text-slate-600 block text-[10px]">Dates & Units:</span>
                          {lastRunResult.oneExactChain.dates} • {lastRunResult.oneExactChain.units}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Persisted Database Evidence */}
                  {lastRunResult.persistedDatabaseEvidence && (
                    <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-[11px] flex items-center justify-between text-slate-800">
                      <div className="flex items-center space-x-2 text-slate-700 font-semibold">
                        <Database className="w-3.5 h-3.5 text-emerald-700" />
                        <span>Live Relational Database Totals:</span>
                      </div>
                      <div className="flex space-x-4 font-mono">
                        <span>
                          Clients:{' '}
                          <strong className="text-slate-900">
                            {lastRunResult.persistedDatabaseEvidence.totalClientsInDb}
                          </strong>
                        </span>
                        <span>
                          Auths:{' '}
                          <strong className="text-slate-900">
                            {lastRunResult.persistedDatabaseEvidence.totalAuthorizationsInDb}
                          </strong>
                        </span>
                        <span>
                          Claims:{' '}
                          <strong className="text-slate-900">
                            {lastRunResult.persistedDatabaseEvidence.totalClaimsInDb}
                          </strong>
                        </span>
                        <span>
                          Payments:{' '}
                          <strong className="text-slate-900">
                            {lastRunResult.persistedDatabaseEvidence.totalPaymentsInDb}
                          </strong>
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'EXCEPTIONS' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center space-x-2">
                    <AlertTriangle className="w-4 h-4 text-amber-800" />
                    <span>Unresolved Staged Exception Rows ({exceptions.length})</span>
                  </h4>
                  <p className="text-[11px] text-slate-700 mt-0.5">
                    Rows quarantined due to missing authorization numbers, ambiguous client matches, or date conflicts.
                  </p>
                </div>
                <button
                  onClick={fetchExceptions}
                  className="px-2.5 py-1 rounded bg-white text-slate-800 text-xs hover:bg-slate-50 border border-slate-300 flex items-center space-x-1"
                >
                  <RefreshCw className="w-3 h-3" />
                  <span>Refresh Queue</span>
                </button>
              </div>

              {exceptions.length === 0 ? (
                <div className="p-8 text-center text-xs text-slate-700 rounded-xl bg-white border border-slate-200">
                  <CheckCircle2 className="w-8 h-8 text-emerald-700 mx-auto mb-2" />
                  <p className="font-semibold text-slate-900">All Import Rows Fully Reconciled</p>
                  <p className="text-[11px] text-slate-600 mt-0.5">
                    Zero held exceptions in the database staging table.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {exceptions.map((ex) => (
                    <div
                      key={ex.id}
                      className="p-3 rounded-xl bg-white border border-amber-200 space-y-2"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs font-mono font-bold text-amber-900">
                              Row #{ex.row_index}
                            </span>
                            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-amber-50 text-amber-900 border border-amber-200 font-bold">
                              {ex.exception_reason}
                            </span>
                            <span className="text-[10px] text-slate-700 font-mono">
                              File: {ex.source_filename}
                            </span>
                          </div>
                          <p className="text-xs text-slate-900 mt-1 font-medium">
                            {ex.exception_details}
                          </p>
                        </div>

                        <div className="flex space-x-1.5">
                          <button
                            onClick={() => setSelectedException(ex)}
                            className="px-2.5 py-1 rounded bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold border border-slate-700"
                          >
                            Resolve / Reconcile
                          </button>
                          <button
                            onClick={() => handleDismissException(ex.id)}
                            className="px-2.5 py-1 rounded bg-white hover:bg-slate-50 text-slate-800 text-xs border border-slate-300"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>

                      {/* Raw Data Preview */}
                      <div className="p-2 rounded bg-slate-50 border border-slate-200 font-mono text-[10px] text-slate-800 overflow-x-auto">
                        {ex.raw_data_json}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Exception Resolution Modal / Drawer */}
              {selectedException && (
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-3 mt-4">
                  <div className="flex justify-between items-center pb-2 border-b border-slate-200">
                    <span className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                      Resolve Exception: Row #{selectedException.row_index} ({selectedException.exception_reason})
                    </span>
                    <button
                      onClick={() => setSelectedException(null)}
                      className="text-slate-600 hover:text-slate-900"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-800 mb-1">
                        Map to Client Chart
                      </label>
                      <select
                        value={resolutionClientId}
                        onChange={(e) => setResolutionClientId(e.target.value)}
                        className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs text-slate-900"
                      >
                        <option value="">-- Select Client from Database --</option>
                        {clients.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.first_name} {c.last_name} ({c.id} • {c.policy_id || 'No Policy'})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-[11px] font-semibold text-slate-800 mb-1">
                        Resolution Notes
                      </label>
                      <input
                        type="text"
                        value={resolutionNotes}
                        onChange={(e) => setResolutionNotes(e.target.value)}
                        placeholder="e.g. Confirmed authorization with Mercy Care"
                        className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs text-slate-900 placeholder:text-slate-500"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end space-x-2 pt-2">
                    <button
                      onClick={() => setSelectedException(null)}
                      className="px-3 py-1.5 rounded-lg bg-white text-slate-800 text-xs border border-slate-300"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleResolveException}
                      disabled={loading}
                      className="px-4 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold border border-slate-700 shadow-xs"
                    >
                      Commit Resolution
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'HISTORY' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center space-x-2">
                    <History className="w-4 h-4 text-slate-700" />
                    <span>Past WellSky Ingestion Runs & Equations</span>
                  </h4>
                  <p className="text-[11px] text-slate-700 mt-0.5">
                    Tamper-evident record of all files imported, row sums, and hash signatures.
                  </p>
                </div>
                <button
                  onClick={fetchPastRuns}
                  className="px-2.5 py-1 rounded bg-white text-slate-800 text-xs hover:bg-slate-50 border border-slate-300 flex items-center space-x-1"
                >
                  <RefreshCw className="w-3 h-3" />
                  <span>Refresh</span>
                </button>
              </div>

              {pastRuns.length === 0 ? (
                <div className="p-8 text-center text-xs text-slate-700 rounded-xl bg-white border border-slate-200">
                  <p>No past import runs found in database.</p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {pastRuns.map((r) => {
                    const eqValid = r.reconciliation_equation_valid === 1;
                    return (
                      <div
                        key={r.id}
                        className="p-3.5 rounded-xl bg-white border border-slate-200 space-y-2"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 text-xs">
                          <div className="flex items-center space-x-2">
                            <span className="font-mono font-bold text-slate-900">{r.id}</span>
                            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-50 text-slate-800 border border-slate-200 font-semibold">
                              {r.report_type}
                            </span>
                            <span
                              className={`text-[10px] font-mono px-2 py-0.5 rounded font-bold ${
                                r.status === 'COMPLETED'
                                  ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                                  : 'bg-amber-50 text-amber-900 border border-amber-200'
                              }`}
                            >
                              {r.status}
                            </span>
                          </div>
                          <span className="text-[11px] font-mono text-slate-700">
                            {r.created_at} • By {r.created_by || 'system'}
                          </span>
                        </div>

                        <div className="text-xs text-slate-800 flex items-center justify-between">
                          <span className="text-[11px] font-mono text-slate-700 truncate max-w-sm">
                            Source: {r.source_filename} (SHA: {r.file_hash?.slice(0, 12)}...)
                          </span>
                          <span
                            className={`text-[11px] font-mono font-semibold ${
                              eqValid ? 'text-emerald-800' : 'text-red-800'
                            }`}
                          >
                            Equation: {eqValid ? 'VALID (100% Accounted)' : 'INVALID'}
                          </span>
                        </div>

                        {/* Breakdown summary */}
                        <div className="grid grid-cols-5 gap-1 text-[11px] font-mono text-center pt-1 border-t border-slate-200">
                          <div className="text-slate-700">
                            Source: <strong className="text-slate-900">{r.source_rows_count}</strong>
                          </div>
                          <div className="text-emerald-800">
                            Accepted: <strong>{r.accepted_count}</strong>
                          </div>
                          <div className="text-purple-800">
                            Duplicate: <strong>{r.duplicate_count}</strong>
                          </div>
                          <div className="text-amber-800">
                            Held: <strong>{r.held_count}</strong>
                          </div>
                          <div className="text-red-800">
                            Rejected: <strong>{r.rejected_count}</strong>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-200 bg-white flex justify-between items-center text-xs">
          <div className="flex items-center space-x-2 text-slate-700 text-[11px]">
            <ShieldCheck className="w-4 h-4 text-emerald-700" />
            <span>Ported Allegiant Architecture: Production Proven Ingestion Engine</span>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white font-semibold border border-slate-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
